/**
 * Custom JavaScript for WTB theme
 */

/**
 * Custom Show Specs Button Component
 * 
 * A custom HTML element that scrolls to and activates the specs tab/accordion.
 * 
 * Usage:
 *   <custom-show-specs-button>
 *     <button type="button" class="link">Show specs</button>
 *   </custom-show-specs-button>
 * 
 * On click, scrolls to the product tabs section and:
 * - Desktop: activates the specs tab (targets #product-specs-tab-button)
 * - Mobile: expands the specs accordion (targets #product-specs-accordion-wrapper)
 */
class CustomShowSpecsButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    
    // Check if we're on mobile (corresponding to theme / product tabs component break point)
    const isMobile = window.innerWidth < 700;
    
    if (isMobile) {
      this.showSpecsMobile();
    } else {
      this.showSpecsDesktop();
    }
  }

  showSpecsDesktop() {
    // Use data attribute instead of ID, since x-tabs component overwrites button IDs
    const specsTabButton = document.querySelector('[data-specs-tab]');
    
    if (!specsTabButton) {
      console.warn('CustomShowSpecsButton: Specs tab button not found');
      return;
    }
    
    // Find the parent x-tabs element
    const tabsContainer = specsTabButton.closest('x-tabs');
    
    if (!tabsContainer) {
      console.warn('CustomShowSpecsButton: Tabs container not found');
      return;
    }
    
    // Scroll to the tabs container with some offset
    this.scrollToElement(tabsContainer, () => {
      // Click the specs tab button to activate it
      // The x-tabs component handles the tab switching on click
      specsTabButton.click();
    });
  }

  showSpecsMobile() {
    const specsAccordionWrapper = document.getElementById('product-specs-accordion-wrapper');
    
    if (!specsAccordionWrapper) {
      console.warn('CustomShowSpecsButton: Specs accordion wrapper not found');
      return;
    }
    
    // Find the accordion-disclosure element
    const accordionDisclosure = specsAccordionWrapper.querySelector('accordion-disclosure');
    
    if (!accordionDisclosure) {
      console.warn('CustomShowSpecsButton: Accordion disclosure not found');
      return;
    }
    
    // Scroll to the accordion
    this.scrollToElement(specsAccordionWrapper, () => {
      // Open the accordion using the component's API
      // The AccordionDisclosure component has an open() method
      if (typeof accordionDisclosure.open === 'function') {
        accordionDisclosure.open();
      } else {
        // Fallback: click the summary to toggle
        const summary = accordionDisclosure.querySelector('summary');
        if (summary) {
          const details = accordionDisclosure.querySelector('details');
          if (details && !details.open) {
            summary.click();
          }
        }
      }
    });
  }

  scrollToElement(element, callback) {
    // Get header height for offset (if sticky header exists)
    const header = document.querySelector('.shopify-section-group-header-group');
    const headerHeight = header ? header.offsetHeight : 0;
    
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const offsetPosition = absoluteElementTop - headerHeight - 20; // 20px extra padding
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
    
    // Execute callback after scroll animation (approximate)
    if (callback) {
      setTimeout(callback, 500);
    }
  }
}

// Register the custom element
if (!window.customElements.get('custom-show-specs-button')) {
  window.customElements.define('custom-show-specs-button', CustomShowSpecsButton);
}

/**
 * Free Shipping Currency Adjustment
 *
 * The theme's free-shipping-bar stores its configured threshold in shop currency cents.
 * Convert that value to the active presentment currency so Markets/local currencies
 * compare cart totals against the equivalent free-shipping amount.
 */
(() => {
  const BASE_THRESHOLD_ATTRIBUTE = 'data-base-threshold';
  const FREE_SHIPPING_BAR_SELECTOR = 'free-shipping-bar';
  let currencyFormatter = null;
  let currencyFormatterKey = '';
  const CURRENCY_LOCALES = {
    BGN: 'bg-BG',
    CHF: 'de-CH',
    CZK: 'cs-CZ',
    DKK: 'da-DK',
    GBP: 'en-GB',
    HUF: 'hu-HU',
    NOK: 'nb-NO',
    PLN: 'pl-PL',
    RON: 'ro-RO',
    SEK: 'sv-SE'
  };

  function getCurrencyRate() {
    const rate = Number.parseFloat(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  function getCurrencyFormatter() {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = CURRENCY_LOCALES[currency] || window.Shopify?.locale || document.documentElement.lang || 'en';
    const formatterKey = `${locale}:${currency}`;

    if (currencyFormatter && currencyFormatterKey === formatterKey) {
      return currencyFormatter;
    }

    currencyFormatterKey = formatterKey;
    currencyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    });

    return currencyFormatter;
  }

  function renderFreeShippingMessage(bar, threshold) {
    const messageElement = bar.querySelector(':scope > span');
    const totalPrice = Number.parseFloat(bar.getAttribute('total-price'));

    if (!messageElement || !Number.isFinite(totalPrice)) {
      return;
    }

    if (totalPrice >= threshold) {
      messageElement.innerHTML = bar.getAttribute('reached-message') || '';
      return;
    }

    const remainingAmount = getCurrencyFormatter()
      .format((threshold - totalPrice) / 100)
      .replace(/\$/g, '$$$$');

    messageElement.innerHTML = (bar.getAttribute('unreached-message') || '').replace(
      new RegExp('({{.*}})', 'g'),
      remainingAmount
    );
  }

  function updateFreeShippingBar(bar) {
    if (!(bar instanceof HTMLElement)) {
      return;
    }

    const currentThreshold = Number.parseFloat(bar.getAttribute('threshold'));
    let baseThreshold = Number.parseFloat(bar.getAttribute(BASE_THRESHOLD_ATTRIBUTE));

    if (!Number.isFinite(baseThreshold)) {
      baseThreshold = currentThreshold;
      bar.setAttribute(BASE_THRESHOLD_ATTRIBUTE, String(baseThreshold));
    }

    if (!Number.isFinite(baseThreshold)) {
      return;
    }

    const convertedThreshold = Math.round(baseThreshold * getCurrencyRate());

    // The native component keeps its initial threshold privately, so custom.js
    // owns the converted attribute and message render after cart/theme updates.
    if (bar.getAttribute('threshold') !== String(convertedThreshold)) {
      bar.setAttribute('threshold', String(convertedThreshold));
    }

    renderFreeShippingMessage(bar, convertedThreshold);
  }

  function updateFreeShippingBars(root = document) {
    root.querySelectorAll?.(FREE_SHIPPING_BAR_SELECTOR).forEach(updateFreeShippingBar);
  }

  function initFreeShippingCurrencyAdjustment() {
    updateFreeShippingBars();

    document.addEventListener('cart:change', () => {
      queueMicrotask(updateFreeShippingBars);
    });

    const observer = new MutationObserver((mutations) => {
      const barsToUpdate = new Set();

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.matches(FREE_SHIPPING_BAR_SELECTOR)) {
            barsToUpdate.add(node);
          }

          node.querySelectorAll?.(FREE_SHIPPING_BAR_SELECTOR).forEach((bar) => {
            barsToUpdate.add(bar);
          });
        });
      });

      if (barsToUpdate.size > 0) {
        queueMicrotask(() => barsToUpdate.forEach(updateFreeShippingBar));
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.customElements.whenDefined('free-shipping-bar').then(initFreeShippingCurrencyAdjustment);
})();

/**
 * Custom Progress Bar Component
 * 
 * A custom HTML element that automatically syncs with its parent free-shipping-bar
 * to display progress visualization. Observes threshold and total-price attributes
 * and updates the progress indicator accordingly.
 */
class CustomProgressBar extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._parentBar = null;
  }

  connectedCallback() {
    // Set ARIA role
    this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    
    // Find parent free-shipping-bar element
    this._parentBar = this.closest('free-shipping-bar');
    
    if (!this._parentBar) {
      console.warn('custom-progress-bar must be a child of free-shipping-bar');
      return;
    }
    
    // Set up MutationObserver to watch parent for attribute changes
    this._observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const attributeName = mutation.attributeName;
          
          if (attributeName === 'threshold') {
            this._updateMaxValue();
          } else if (attributeName === 'total-price') {
            this._updateCurrentValue();
          }
        }
      });
    });
    
    // Start observing parent
    this._observer.observe(this._parentBar, {
      attributes: true,
      attributeFilter: ['threshold', 'total-price']
    });
    
    // Trigger initial update
    this._updateMaxValue();
    this._updateCurrentValue();
  }

  disconnectedCallback() {
    // Clean up observer
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._parentBar = null;
  }

  /**
   * Update the maximum value (threshold) from parent
   * @private
   */
  _updateMaxValue() {
    if (!this._parentBar) return;
    
    const threshold = this._parentBar.getAttribute('threshold');
    if (threshold) {
      this.setAttribute('aria-valuemax', threshold);
      this._updateProgress();
    }
  }

  /**
   * Update the current value (total price) from parent
   * @private
   */
  _updateCurrentValue() {
    if (!this._parentBar) return;
    
    const totalPrice = this._parentBar.getAttribute('total-price');
    if (totalPrice) {
      this.setAttribute('aria-valuenow', totalPrice);
      this._updateProgress();
    }
  }

  /**
   * Calculate and update the progress CSS variable
   * @private
   */
  _updateProgress() {
    const valueNow = parseFloat(this.getAttribute('aria-valuenow')) || 0;
    const valueMax = parseFloat(this.getAttribute('aria-valuemax')) || 1;
    
    // Calculate progress (0 to 1), capped at 1 (100%)
    const progress = Math.min(valueNow / valueMax, 1);
    
    // Update CSS custom property for visual display
    this.style.setProperty('--progress', progress);
  }
}

// Register the custom element
if (!window.customElements.get('custom-progress-bar')) {
  window.customElements.define('custom-progress-bar', CustomProgressBar);
}

/**
 * Custom Variant SKU Info Component
 *
 * Keeps SKU-adjacent variant metafields in sync with the active product variant.
 */
class CustomVariantSkuInfo extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.variantMap = new Map();
  }

  connectedCallback() {
    this.disconnectedCallback();
    this.abortController = new AbortController();
    this.init();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  init() {
    const dataEl = this.querySelector('[data-variant-sku-info]');
    const form = this.form;

    if (!dataEl) return;

    try {
      const variants = JSON.parse(dataEl.textContent || '[]');
      this.variantMap = new Map(
        variants.map((variant) => [parseInt(variant.id, 10), variant])
      );
    } catch (error) {
      return;
    }

    this.update(this.activeVariantId);

    if (form) {
      form.addEventListener('variant:change', (event) => {
        const variantId = event.detail?.variant?.id;
        this.update(variantId ? parseInt(variantId, 10) : this.activeVariantId);
      }, { signal: this.abortController.signal });
    }
  }

  get form() {
    const formId = this.dataset.productFormId;
    return formId ? document.getElementById(formId) : null;
  }

  get activeVariantId() {
    const formIdValue = this.form?.elements?.id?.value;
    const fallbackVariant = this.variantMap.keys().next().value;
    return formIdValue ? parseInt(formIdValue, 10) : fallbackVariant;
  }

  update(variantId) {
    const variant = this.variantMap.get(parseInt(variantId, 10));

    if (!variant) {
      this.setVisible(false);
      return;
    }

    const sku = this.normalizeValue(variant.sku);
    const productName = this.normalizeValue(variant.productName);
    const weight = this.normalizeValue(variant.weight);

    this.updateRow('[data-variant-sku-row]', '[data-variant-sku-value]', sku);
    this.updateRow('[data-variant-product-name-row]', '[data-variant-product-name]', productName);
    this.updateRow('[data-variant-weight-row]', '[data-variant-weight]', weight ? `${weight} g` : '');

    this.setVisible(Boolean(sku || productName || weight));
  }

  updateRow(rowSelector, valueSelector, value) {
    const row = this.querySelector(rowSelector);
    const valueEl = this.querySelector(valueSelector);

    if (!row || !valueEl) return;

    valueEl.textContent = value;
    row.hidden = !value;
  }

  normalizeValue(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  setVisible(isVisible) {
    this.hidden = !isVisible;

    const blockItem = this.closest('.product-info__block-item');
    if (blockItem) {
      blockItem.hidden = !isVisible;
    }
  }
}

if (!window.customElements.get('custom-variant-sku-info')) {
  window.customElements.define('custom-variant-sku-info', CustomVariantSkuInfo);
}

/**
 * Custom Variant Picker Downstream Auto-Select
 *
 * For opted-in pill selectors, when a customer creates a non-existent option combination,
 * select the first available downstream option combination. Sold-out variants still exist,
 * so they remain selected.
 */
class CustomVariantDownstreamAutoSelect extends HTMLElement {
  constructor() {
    super();
    this.variantPicker = null;
    this.availabilityData = null;
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    this.variantPicker = this.closest('variant-picker');

    if (!this.variantPicker) {
      return;
    }

    this.variantPicker.addEventListener('change', this.handleChange, true);
  }

  disconnectedCallback() {
    if (this.variantPicker) {
      this.variantPicker.removeEventListener('change', this.handleChange, true);
    }

    this.variantPicker = null;
    this.availabilityData = null;
  }

  handleChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.matches('input[type="radio"][data-option-position]')) {
      return;
    }

    // Only apply to pill/block/swatch-style controls, not dropdown popover controls.
    if (!input.closest('.variant-picker__option-values')) {
      return;
    }

    const changedPosition = parseInt(input.getAttribute('data-option-position'), 10);
    const form = this.getVariantPickerForm(input);
    const availabilityData = this.getAvailabilityData();

    if (!Number.isFinite(changedPosition) || !form || !availabilityData?.variants?.length) {
      return;
    }

    const selectedOptionValues = this.getSelectedOptionValues(form);

    if (this.combinationExists(availabilityData.variants, selectedOptionValues)) {
      return;
    }

    const fallbackVariant = availabilityData.variants.find((variant) => {
      return variant.available && this.variantMatchesThroughPosition(variant, selectedOptionValues, changedPosition);
    });

    if (!fallbackVariant) {
      return;
    }

    this.selectDownstreamOptionValues(form, fallbackVariant.optionValues, changedPosition);
  }

  getVariantPickerForm(input) {
    const formId = this.variantPicker?.getAttribute('form-id');

    return formId ? document.forms[formId] : input.form;
  }

  getAvailabilityData() {
    if (this.availabilityData !== null) {
      return this.availabilityData;
    }

    const dataElement = this.variantPicker?.querySelector('script[data-custom-variant-availability]');

    if (!dataElement) {
      return null;
    }

    try {
      const data = JSON.parse(dataElement.textContent || '{}');

      data.variants = Array.isArray(data.variants) ? data.variants.map((variant) => ({
        ...variant,
        optionValues: Array.isArray(variant.optionValues) ? variant.optionValues.map(String) : []
      })) : [];

      this.availabilityData = data;
      return data;
    } catch (error) {
      return null;
    }
  }

  getSelectedOptionValues(form) {
    return Array.from(form.elements).reduce((selectedOptionValues, element) => {
      if (element.matches?.('input[data-option-position]:checked')) {
        selectedOptionValues.set(
          parseInt(element.getAttribute('data-option-position'), 10),
          String(element.value)
        );
      }

      return selectedOptionValues;
    }, new Map());
  }

  combinationExists(variants, selectedOptionValues) {
    return variants.some((variant) => {
      return variant.optionValues.every((optionValue, index) => {
        return selectedOptionValues.get(index + 1) === optionValue;
      });
    });
  }

  variantMatchesThroughPosition(variant, selectedOptionValues, changedPosition) {
    for (let position = 1; position <= changedPosition; position++) {
      if (selectedOptionValues.get(position) !== variant.optionValues[position - 1]) {
        return false;
      }
    }

    return true;
  }

  selectDownstreamOptionValues(form, optionValues, changedPosition) {
    const downstreamInputs = [];

    for (const [index, optionValue] of optionValues.entries()) {
      const position = index + 1;

      if (position <= changedPosition) {
        continue;
      }

      const matchingInput = Array.from(form.elements).find((element) => {
        return element.matches?.('input[type="radio"][data-option-position]')
          && parseInt(element.getAttribute('data-option-position'), 10) === position
          && String(element.value) === optionValue
          && !element.disabled;
      });

      if (!matchingInput) {
        return;
      }

      downstreamInputs.push(matchingInput);
    }

    downstreamInputs.forEach((input) => {
      input.checked = true;
    });
  }
}

if (!window.customElements.get('custom-variant-downstream-auto-select')) {
  window.customElements.define('custom-variant-downstream-auto-select', CustomVariantDownstreamAutoSelect);
}

/**
 * Fit Right Floating Bar
 *
 * Always keep the floating element visible when rendered.
 */
(function initFitRightFloatingBar() {
  const bar = document.getElementById('fit-right-floating-bar');

  if (!bar) return;
  bar.classList.add('is-visible');
})();

/**
 * EU legal-guarantee notice modal.
 * Self-contained so the snippet can be reused outside the product page.
 */
if (!customElements.get('legal-guarantee-notice')) {
  class LegalGuaranteeNotice extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-legal-guarantee-notice-open]');
      this.modal = this.querySelector('.legal-guarantee-notice__modal');
      if (!this.trigger || !this.modal) return;

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.modal.addEventListener('click', this.onModalClick);
    }

    disconnectedCallback() {
      this.close();
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      if (this.modal) this.modal.removeEventListener('click', this.onModalClick);
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    onModalClick(event) {
      if (event.target.closest('[data-legal-guarantee-notice-close]')) {
        this.close();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') this.close();
    }

    open() {
      if (!this.modal || this.isOpen) return;

      this.modalParent = this.modal.parentNode;
      this.modalNext = this.modal.nextSibling;
      document.body.appendChild(this.modal);
      this.modal.classList.add('is-open');
      this.modal.removeAttribute('hidden');
      document.body.classList.add('legal-guarantee-notice-open');
      document.addEventListener('keydown', this.onKeyDown);
      this.isOpen = true;

      const closeButton = this.modal.querySelector('.legal-guarantee-notice__close');
      if (closeButton) closeButton.focus();
    }

    close() {
      if (!this.modal || !this.isOpen) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('legal-guarantee-notice-open');
      document.removeEventListener('keydown', this.onKeyDown);

      if (this.modalParent) {
        if (this.modalNext && this.modalNext.parentNode === this.modalParent) {
          this.modalParent.insertBefore(this.modal, this.modalNext);
        } else {
          this.modalParent.appendChild(this.modal);
        }
        this.modalParent = null;
        this.modalNext = null;
      }

      this.isOpen = false;
      if (this.trigger) this.trigger.focus();
    }
  }

  customElements.define('legal-guarantee-notice', LegalGuaranteeNotice);
}
